import { useInvalidate, useNotification } from "@refinedev/core";
import { LoginOutlined, SettingOutlined } from "@ant-design/icons";
import { Alert, Button, Drawer, Form, Input, Select, Tabs, Typography } from "antd";
import axios from "axios";
import { useState, type FC } from "react";
import { axiosInstance } from "../../../api/client";
import { useWpAuthorize } from "../../../hooks/use-wp-authorize";
import type { Environment } from "../../../types";
import { ENV_OPTIONS } from "../constants";

interface EnvironmentDrawerProps {
  projectId: string | null;
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
}

export const EnvironmentDrawer: FC<EnvironmentDrawerProps> = ({ projectId, open, onClose, isMobile }) => {
  const [form] = Form.useForm();
  const [wpAuthError, setWpAuthError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<"browser" | "manual">("browser");

  const invalidate = useInvalidate();
  const { open: notify } = useNotification();
  const { authorize: authorizeWp, isAuthorizing } = useWpAuthorize();

  const handleClose = () => {
    onClose();
    form.resetFields();
    setWpAuthError(null);
    setActiveTab("browser");
  };

  const handleWpAuthorize = async () => {
    const url = form.getFieldValue("url");
    if (!url) {
      setWpAuthError("Please enter a WordPress URL first.");
      return;
    }

    try {
      // Validate only name/url (not username/password): those live in the "manual" tab,
      // which never mounts unless the user visits it, so they're never registered with the
      // form and setFieldsValue + form.submit() would silently drop them.
      const { name } = await form.validateFields(["name", "url"]);
      const credentials = await authorizeWp(url);
      await handleSubmit({ name, url, username: credentials.userLogin, password: credentials.password });
    } catch (error) {
      if (error instanceof Error) setWpAuthError(error.message);
    }
  };

  const handleSubmit = async (values: { name: string; url: string; username?: string; password?: string }) => {
    if (!projectId) return;
    setSubmitting(true);
    try {
      const res = await axiosInstance.post<Environment>(
        `/projects/${projectId}/environments`,
        { name: values.name, url: values.url }
      );
      if (values.username && values.password) {
        await axiosInstance.put(
          `/projects/${projectId}/environments/${res.data.id}/credentials`,
          { username: values.username, password: values.password }
        );
      }
      await invalidate({ resource: "projects", invalidates: ["list"] });
      handleClose();
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 403) {
        notify?.({
          type: "error",
          message: "Free plan limit reached",
          description: "Upgrade to Pro to add more than 3 environments per project.",
        });
      } else {
        notify?.({ type: "error", message: "Failed to add environment" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer title="Add environment" open={open} onClose={handleClose} width={isMobile ? "100%" : 480}>
      <Form form={form} layout="vertical" onFinish={(values) => void handleSubmit(values)}>
        <Form.Item
          name="name"
          label="Environment"
          rules={[{ required: true, message: "Environment is required" }]}
        >
          <Select options={ENV_OPTIONS} placeholder="Select an environment" />
        </Form.Item>
        <Form.Item
          name="url"
          label="WordPress URL"
          rules={[
            { required: true, message: "URL is required" },
            { type: "url", message: "Must be a valid URL" },
          ]}
        >
          <Input placeholder="https://example.com" />
        </Form.Item>

        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as "browser" | "manual")}
          items={[
            {
              key: "browser",
              label: (
                <span>
                  <LoginOutlined style={{ marginRight: 6 }} />
                  Authorize in browser
                </span>
              ),
              children: (
                <div style={{ textAlign: "center", padding: "32px 0" }}>
                  <div style={{ marginBottom: 16 }}>
                    <LoginOutlined style={{ fontSize: 42, color: "#1677ff" }} />
                  </div>
                  <Typography.Text type="secondary" style={{ display: "block", marginBottom: 24, fontSize: 14, lineHeight: 1.6 }}>
                    We'll open WordPress in your browser to authorize Loopress securely.
                    <br />
                    No need to manually create an application password.
                  </Typography.Text>
                  <Button
                    type="primary"
                    size="medium"
                    block
                    onClick={() => void handleWpAuthorize()}
                    loading={isAuthorizing || submitting}
                    icon={<LoginOutlined />}
                  >
                    {isAuthorizing ? "Authorizing..." : "Authorize in browser"}
                  </Button>
                  {wpAuthError && (
                    <Alert
                      type="error"
                      message={wpAuthError}
                      showIcon
                      style={{ marginTop: 16 }}
                    />
                  )}
                </div>
              ),
            },
            {
              key: "manual",
              label: (
                <span>
                  <SettingOutlined style={{ marginRight: 6 }} />
                  Enter credentials manually
                </span>
              ),
              children: (
                <div style={{ padding: "32px 0" }}>
                  <Typography.Text type="secondary" style={{ display: "block", marginBottom: 16, fontSize: 13 }}>
                    If you prefer to create an application password manually in WordPress, you can enter it here.
                  </Typography.Text>
                  <Form.Item
                    name="username"
                    label="Username"
                    rules={[
                      { required: true, message: "Username is required" },
                    ]}
                  >
                    <Input placeholder="admin" />
                  </Form.Item>
                  <Form.Item
                    name="password"
                    label="Application password"
                    extra="Generate one in WordPress: Users, Profile, Application Passwords."
                    rules={[
                      { required: true, message: "Application password is required" },
                    ]}
                  >
                    <Input.Password placeholder="xxxx xxxx xxxx xxxx xxxx xxxx" />
                  </Form.Item>
                  <Button type="primary" htmlType="submit" size="medium" loading={submitting} block>
                    Add environment
                  </Button>
                </div>
              ),
            },
          ]}
        />
      </Form>
    </Drawer>
  );
};
